import React, { forwardRef, type SVGAttributes } from 'react';

/*
 * ButtonArrow — Button-internal arrow that grows on hover.
 *
 * Mechanism (left-anchored growth):
 *   shaft   <line>      — fixed (0,12)→(12,12); scaleX 1→1.333 with origin at left
 *                         end. vector-effect non-scaling-stroke keeps stroke 1.5px.
 *   chevron <polyline>  — fixed at (6.46,6)-(12.46,12)-(6.46,18); translateX 0→3.54px.
 *
 * Both animate at the same rate so the shaft RIGHT endpoint and chevron TIP end up
 * coincident at (16,12) — arrow grows in length, left endpoint stays anchored.
 *
 * Animation rules live in src/theme/components.ts (`& .hd-btn-arrow__*`).
 * SVG overflow:visible because scale + translate can push graphics past viewBox.
 *
 * No `as` prop — the `<line>`/`<polyline>` children only render validly inside
 * an SVG context, so the root can't be swapped to another element (same
 * constraint as Spinner elsewhere in the library).
 */
export type ButtonArrowProps = SVGAttributes<SVGSVGElement>;

export const ButtonArrow = forwardRef<SVGSVGElement, ButtonArrowProps>(
  function ButtonArrow({ className, style, ...rest }, ref) {
  const rootClass = ['hd-btn-arrow', className].filter(Boolean).join(' ');
  return (
  <svg
    ref={ref}
    className={rootClass}
    width="18"
    height="24"
    viewBox="0 0 18 24"
    fill="none"
    aria-hidden
    style={{ overflow: 'visible', ...style }}
    {...rest}
  >
    <line
      className="hd-btn-arrow__shaft"
      x1="0"
      y1="12"
      x2="12"
      y2="12"
      stroke="currentColor"
      strokeWidth="1.5"
      vectorEffect="non-scaling-stroke"
    />
    <polyline
      className="hd-btn-arrow__tip"
      points="6.46143,6 12.4614,12 6.46143,18"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
  );
  },
);

ButtonArrow.displayName = 'ButtonArrow';
